import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import api from '../services/api';
import { useAppData } from '../contexts/AppDataContext';

/**
 * The account owner's sample "Demo Farm" (GET/POST/DELETE /demo-farm). Adding or removing it
 * changes the farm list and most totals, so both refresh the farms and every loaded query.
 */
export default function useDemoFarm({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const { refreshFarms, refreshSubscription } = useAppData();

  const query = useQuery({
    queryKey: ['demo-farm'],
    queryFn: async () => (await api.get('/demo-farm')).data,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const refreshAll = async () => {
    await Promise.all([refreshFarms(), refreshSubscription?.()]);
    queryClient.invalidateQueries();
  };

  const addDemo = useMutation({
    mutationFn: async () => (await api.post('/demo-farm')).data,
    onSuccess: async () => {
      await refreshAll();
      message.success('Demo farm added. Pick "Demo Farm (sample)" in any farm filter to explore it.');
    },
    onError: (error) => message.error(error.response?.data?.message || 'Could not add the demo farm.'),
  });

  const removeDemo = useMutation({
    mutationFn: async () => (await api.delete('/demo-farm')).data,
    onSuccess: async () => {
      // Drop the demo farm from the cache right away so the remove button disappears.
      queryClient.setQueryData(['demo-farm'], (old) => (old ? { ...old, farm: null } : old));
      await refreshAll();
      message.success('Demo farm removed. Your own farms were not changed.');
    },
    onError: (error) => message.error(error.response?.data?.message || 'Could not remove the demo farm.'),
  });

  return {
    demoFarm: query.data?.farm || null,
    canManage: query.data?.can_manage === true,
    isLoading: query.isLoading,
    addDemo,
    removeDemo,
  };
}
